import { useEffect, useState } from 'react';
import {
    Box,
    Tabs,
    Tab,
    Chip,
    Grid,
    Menu,
    MenuItem,
    Stack,
    Button,
    CircularProgress
} from '@mui/material';
import SearchToolbar from '../../components/SearchToolbar/SearchToolbar';
import SectionHeader from '../../components/SectionHeader';
import { useTranslation } from 'react-i18next';
import { useIsMobile } from '../../hooks/useIsMobile';
import NoTableDataImageSimp from '../../assets/image/empty-state-establecimientos-simp.png';
import NoTableDataImageBO from '../../assets/image/empty-state-establecimientos-backoffice.png';
import { useRoles } from '../../hooks/useRoles';
import ConvocatoriasGrid from './components/ConvocatoriasGrid';
import { ConvocatedFilters } from './components/convocatedFilters';
import { useNavigate, useParams } from 'react-router-dom';
import { useConvocatorias } from './hooks/useConvocatorias';
import { useLocation } from 'react-router-dom';
import BackdropComponent from '../../components/Backdrop/BackdropComponent';

const ConvocatoriasIndex = ({ isHome }: { isHome?: boolean }) => {
    const { t } = useTranslation('periodicos');
    const { isAdminBO } = useRoles();
    const { isMobile, isTablet } = useIsMobile();
    const {
        convocatoriasData,
        isFetching,
        filters,
        setFilters,
        handleSubmitSearch,
        handleReload,
        clearFilters,
        extraFilters,
        handleChangeExtraFilters,
        handleClickAction,
        anchorEl,
        menuOpen,
        handleCloseMenu,
        handleGoToConvocados,
        selectedConvocatoria,
        handleGeneratePdfConvocatorias,
        handleGeneratePdfResultados,
        isGeneratingConvocatoriaPdf,
        isGeneratingResultadosPdf
    } = useConvocatorias();

    const location = useLocation();
    const { idConvocatoria, fechaAlta, idOrganismoCentral, cuit, nombreOrganismoCentral } =
        location.state || {};

    const getActionLabel = (actionKey: string): string => {
        if (!selectedConvocatoria) return t(actionKey);

        const estado = selectedConvocatoria.id_estado_convocatoria;

        if (estado === 3) {
            switch (actionKey) {
                case 'table.actions.generateCall':
                    return t('table.actions.generatePDFCalls');
                case 'table.actions.generateResults':
                    return t('table.actions.generatePDFResults');
                default:
                    return t(actionKey);
            }
        }

        if (estado === 1) {
            switch (actionKey) {
                case 'table.actions.generateCall':
                    return t('table.actions.generatePDFCalls');
                default:
                    return t(actionKey);
            }
        }

        return t(actionKey);
    };

    const translate = (key: string): string => t(key) as string;
    return (
        <>
            <Grid container>
                <Grid item xs={12}>
                    <SectionHeader>
                        <SectionHeader.Title>{t('header.titleConvocatories')}</SectionHeader.Title>
                    </SectionHeader>
                </Grid>
                <Grid item xs={12}>
                    <SearchToolbar
                        inputSearchName=""
                        onChange={() => {}}
                        onClick={handleSubmitSearch}
                        showSearchInput={false}
                        clearFilters={clearFilters}
                    >
                        <ConvocatedFilters
                            extraFilters={extraFilters}
                            handleChangeExtraFilters={handleChangeExtraFilters}
                        />
                    </SearchToolbar>
                </Grid>
            </Grid>

            <Grid
                container
                direction="row"
                justifyContent="center"
                alignItems="flex-start"
                spacing={3}
            >
                {isMobile || isTablet ? (
                    ''
                ) : (
                    <Grid item xs={12}>
                        <Box sx={{ mb: 2 }}>
                            {(convocatoriasData?.data?.length === 0 ||
                                convocatoriasData?.data === undefined) &&
                            !isFetching ? (
                                <Box
                                    my={1}
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="center"
                                    flexDirection="column"
                                >
                                    <Stack
                                        direction="column"
                                        justifyContent="center"
                                        alignItems="center"
                                        sx={{
                                            '& h4': { fontSize: '18px' },
                                            '& p': {
                                                maxWidth: '360px',
                                                textAlign: 'center',
                                                margin: 0,
                                                fontSize: '16px'
                                            }
                                        }}
                                    >
                                        <img
                                            src={
                                                isAdminBO
                                                    ? NoTableDataImageBO
                                                    : NoTableDataImageSimp
                                            }
                                            alt=""
                                        />
                                        <h4>{t('table.noData.title')}</h4>
                                        <p>{t('table.noData.description')}</p>
                                    </Stack>
                                </Box>
                            ) : (
                                <ConvocatoriasGrid
                                    items={convocatoriasData}
                                    filters={filters}
                                    setFilters={setFilters}
                                    isFetching={isFetching}
                                    selectedEmployee={null}
                                    selectedIds={[]}
                                    handleSelectEmployee={() => {}}
                                    handleClickAction={handleClickAction}
                                    translate={translate}
                                />
                            )}
                        </Box>
                    </Grid>
                )}
            </Grid>
            <Menu anchorEl={anchorEl} open={menuOpen} onClose={handleCloseMenu}>
                <MenuItem
                    onClick={() =>
                        handleGeneratePdfConvocatorias(selectedConvocatoria?.id_convocatoria)
                    }
                >
                    <Stack direction="row" alignItems="center" spacing={1}>
                        {isGeneratingConvocatoriaPdf && <CircularProgress size={16} />}
                        <span>{getActionLabel('table.actions.generateCall')}</span>
                    </Stack>
                </MenuItem>

                {selectedConvocatoria?.id_estado_convocatoria !== 2 && (
                    <MenuItem
                        disabled={isGeneratingResultadosPdf}
                        onClick={() =>
                            handleGeneratePdfResultados(selectedConvocatoria?.id_convocatoria)
                        }
                    >
                        <Stack direction="row" alignItems="center" spacing={1}>
                            {isGeneratingResultadosPdf && <CircularProgress size={16} />}
                            <span>{getActionLabel('table.actions.generateResults')}</span>
                        </Stack>
                    </MenuItem>
                )}

                <MenuItem onClick={handleGoToConvocados}>
                    {t('table.actions.seeConvocados')}
                </MenuItem>
            </Menu>
            <BackdropComponent loading={isGeneratingConvocatoriaPdf || isGeneratingResultadosPdf} />
        </>
    );
};

export default ConvocatoriasIndex;
